// @flow strict
import * as React from 'react';
import { connect } from 'react-redux';
import debounce from 'debounce';
import { Button } from 'moods-react/Button';
import classnames from 'classnames';
import { Spinner, spinnerSizes } from 'moods-react/Spinner';
import createReducer from '../../helpers/createReducer';
import { buildAndShowSuccessToast, buildAndShowErrorToast } from '../core/actions/toast/ToastActions';
import {
  getAccountSettings,
  updateAccountSettings,
} from '../../clients/AccountSettingsService/AccountSettingsServiceClient';
import './SettingsPage.scss';

import { ChipBuilder } from '../../components/chipBuilder/chipBuilder';
import { CollapsibleContent } from '../../components/CollapsibleContent/CollapsibleContent';

const css = 'account-settings';

class Component extends React.Component {
  UNSAFE_componentWillMount() {
    this.props.loadSettings();
  }

  renderInvoiceSettings(props) {
    const {
      isInvoicingEnabled,
      toggleValue,
      isCostCentreRequired,
      updateCostCentres,
      costCentres,
      isPaymentRestrictedToInvoicing,
      isPONumberEnabled,
      isPONumberRequired,
    } = props;

    return (
      <CollapsibleContent
        title="Invoicing preferences"
        infoText="Enable invoicing, restrict payments to invoice, PO numbers, cost centers"
      >
        <div className={`${css}__section`}>
          <div className={`${css}__subsection`}>
            <div className={`${css}__inputwrapper ${css}__inputtoggle`}>
              <div className={`${css}__inputheader ${css}--margin-none`}>
                <h6 className={`${css}--margin-none`}>Invoicing</h6>
              </div>
              <div className={`${css}--position-right`}>
                <ToggleInput
                  label="Enable Invoicing"
                  checked={isInvoicingEnabled}
                  onToggle={() => toggleValue('isInvoicingEnabled')}
                />
              </div>
            </div>
            <div className={`${css}__inputinfo`}>
              <p className={`${css}__inputinfo-para`}>Enables/disables invoicing for all users on this account.</p>
            </div>
          </div>

          <div className={`${css}__subsection`}>
            <div className={`${css}__inputwrapper`}>
              <div
                className={classnames({
                  [`${css}__inputheader`]: true,
                  [`${css}__inputheader--disabled`]: !isInvoicingEnabled,
                })}
              >
                <h6>General</h6>
              </div>
              <div className={`${css}__inputcontent`}>
                <input
                  className={classnames(
                    'checkbox',
                    { 'is-disabled': !isInvoicingEnabled },
                    `${css}__inputcontent-checkbox`,
                  )}
                  data-form="restricted"
                  disabled={!isInvoicingEnabled}
                  type="checkbox"
                  id="restrictedpayment-checkbox"
                  checked={isPaymentRestrictedToInvoicing}
                  onChange={() => {
                    toggleValue('isPaymentRestrictedToInvoicing');
                  }}
                />
                <label htmlFor="restrictedpayment-checkbox" className={`label ${css}__inputcontent-label`}>
                  Restrict payment to invoice only
                </label>
              </div>
            </div>
            <div className={`${css}__inputinfo`}>
              <p className={`${css}__inputinfo-para`}>
                When enabled, all users on this account will be restricted to pay by invoice.
              </p>
            </div>
          </div>

          <div className={`${css}__subsection`}>
            <div className={`${css}__inputwrapper`}>
              <div
                className={classnames({
                  [`${css}__inputheader`]: true,
                  [`${css}__inputheader--disabled`]: !isInvoicingEnabled,
                })}
              >
                <h6>PO Numbers</h6>
              </div>
              <div className={`${css}__inputcontent--multiselect ${css}__inputcontent`}>
                <div className={`${css}__input-spacer`}>
                  <input
                    className={classnames(
                      'checkbox',
                      { 'is-disabled': !isInvoicingEnabled },
                      `${css}__inputcontent-checkbox`,
                    )}
                    data-form="po-enabled"
                    disabled={!isInvoicingEnabled}
                    type="checkbox"
                    id="ponumber-enabled-checkbox"
                    checked={isPONumberEnabled}
                    onChange={() => {
                      toggleValue('isPONumberEnabled');
                    }}
                  />
                  <label htmlFor="ponumber-enabled-checkbox" className={`label ${css}__inputcontent-label`}>
                    Display PO number on checkout
                  </label>
                </div>
                <div className={`${css}__input-spacer`}>
                  <input
                    className={classnames(
                      'checkbox',
                      { 'is-disabled': !isInvoicingEnabled || !isPONumberEnabled },
                      `${css}__inputcontent-checkbox`,
                    )}
                    data-form="po-required"
                    disabled={!isInvoicingEnabled || !isPONumberEnabled}
                    type="checkbox"
                    id="ponumber-required-checkbox"
                    checked={isPONumberRequired}
                    onChange={() => {
                      toggleValue('isPONumberRequired');
                    }}
                  />
                  <label htmlFor="ponumber-required-checkbox" className={`label ${css}__inputcontent-label`}>
                    PO number is required
                  </label>
                </div>
              </div>
            </div>
            <div className={`${css}__inputinfo`}>
              <p className={`${css}__inputinfo-para`}>
                Enable/disable PO number field, which can be optional or required.
              </p>
            </div>
          </div>

          <div className={`${css}__subsection`}>
            <div className={`${css}__inputwrapper`}>
              <div
                className={classnames({
                  [`${css}__inputheader`]: true,
                  [`${css}__inputheader--disabled`]: !isInvoicingEnabled,
                })}
              >
                <h6>Cost Centers</h6>
              </div>
              <div className={`${css}__inputcontent`}>
                <div className={`${css}__chipwrapper`}>
                  <ChipBuilder
                    data-form="costcentres"
                    disabled={!isInvoicingEnabled}
                    placeholder="Enter cost centers separated by comma or newline. Cost centers will be available for selection at checkout"
                    onChange={updateCostCentres}
                    chips={costCentres.map((costCentre) => costCentre.label)}
                  />
                </div>
                <input
                  className={classnames(
                    'checkbox',
                    { 'is-disabled': !isInvoicingEnabled || costCentres.length === 0 },
                    `${css}__inputcontent-checkbox`,
                  )}
                  data-form="costcentre-required"
                  disabled={!isInvoicingEnabled || costCentres.length === 0}
                  type="checkbox"
                  id="costcentre-required-checkbox"
                  checked={isCostCentreRequired}
                  onChange={() => {
                    toggleValue('isCostCentreRequired');
                  }}
                />
                <label htmlFor="costcentre-required-checkbox" className={`label ${css}__inputcontent-label`}>
                  Cost center selection is required
                </label>
              </div>
            </div>
            <div className={`${css}__inputinfo`}>
              <p className={`${css}__inputinfo-para`}>
                This setting will add a cost center dropdown to the checkout process to all users on this account. Cost
                center numbers need to be separated by comma. The selection can be required.
              </p>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    );
  }

  renderPaymentSettings(props) {
    const { currencies, toggleCurrency } = props;
    const stores = currencies.map((currency) => {
      const currencyInputId = `currency-checkbox-${currency.code}`;
      return (
        <div className={`${css}__input-spacer`} key={`currency_${currency.code}`}>
          <input
            className={classnames('checkbox', `${css}__inputcontent-checkbox`)}
            type="checkbox"
            id={currencyInputId}
            checked={currency.enabled}
            onChange={() => {
              toggleCurrency(currency.code);
            }}
          />
          <label htmlFor={currencyInputId} className={`label ${css}__inputcontent-label`}>
            {currency.code}
          </label>
        </div>
      );
    });

    return (
      <CollapsibleContent title="Payment" infoText="Restrict currency">
        <div className={`${css}__section`}>
          <div className={`${css}__subsection`}>
            <div className={`${css}__inputwrapper`}>
              <div
                className={classnames({
                  [`${css}__inputheader`]: true,
                })}
              >
                <h6>Restrict currency</h6>
              </div>
              <div className={`${css}__inputcontent--multiselect ${css}__inputcontent`}>{stores}</div>
            </div>
            <div className={`${css}__inputinfo`}>
              <p className={`${css}__inputinfo-para`}>
                All currencies are enabled by default; to restrict the currencies that can be used on this account,
                select one or multiple.
              </p>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    );
  }

  renderUpdateButton(props) {
    const { isUpdating, updateSettings } = props;

    return (
      <div className={`${css}__btnsection`}>
        {isUpdating ? (
          <div className={`${css}__btnspinner`}>
            <Spinner white data-form="update-spinner" size={spinnerSizes.MEDIUM} />
          </div>
        ) : (
          <Button data-form="update" isFullWidth onClick={updateSettings}>
            Update settings
          </Button>
        )}
      </div>
    );
  }

  render() {
    const { isLoading, availableSettings } = this.props;
    const availableSettingsSize = Object.keys(availableSettings).length;

    const loadingScreen = (
      <div className={`${css}__loading`} data-form="loading-screen">
        <Spinner size={spinnerSizes.LARGE} />
      </div>
    );

    const emptyState = (
      <div className={`${css}__section ${css}__sectionheader`}>
        <h4 className={`${css}__subheader`}>No preferences available</h4>
      </div>
    );

    const main = (
      <div className={css}>
        <div className={`${css}__header`}>
          <h3 className={`${css}__headertext`}>Account Settings</h3>
        </div>
        <main>
          <div className={`${css}__page`}>
            {availableSettings.editableAccountSettings.indexOf('INVOICING') > -1 &&
              this.renderInvoiceSettings(this.props)}
            {!isLoading &&
              availableSettings.editableAccountSettings.indexOf('PAYMENT') > -1 &&
              this.renderPaymentSettings(this.props)}
            {availableSettingsSize > 0 && this.renderUpdateButton(this.props)}
            {availableSettingsSize === 0 && emptyState}
          </div>
        </main>
      </div>
    );

    return isLoading ? loadingScreen : main;
  }
}

const initialSettingPageState = {
  isLoading: true,
  isUpdating: false,
  isInvoicingEnabled: false,
  isPONumberEnabled: false,
  isPONumberRequired: false,
  isPaymentRestrictedToInvoicing: false,
  costCentres: [],
  isCostCentreRequired: false,
};

const ACTION_TOGGLE = 'TOGGLE_VALUE';
const ACTION_TOGGLE_CURRENCY = 'ACTION_TOGGLE_CURRENCY';
const ACTION_LOAD_SETTINGS = 'LOAD_SETTINGS';
const ACTION_LOAD_SETTINGS_SUCCESS = 'LOAD_SETTINGS_SUCCESS';
const ACTION_LOAD_SETTINGS_FAILURE = 'LOAD_SETTINGS_FAILURE';
const ACTION_UPDATE_SETTINGS = 'UPDATE_SETTINGS';
const ACTION_UPDATE_SETTINGS_SUCCESS = 'UPDATE_SETTINGS_SUCCESS';
const ACTION_UPDATE_SETTINGS_FAILURE = 'UPDATE_SETTINGS_FAILURE';
const ACTION_UPDATE_COSTCENTRES = 'UPDATE_COSTCENTRES';

const debouncedUpdateCostCentres = debounce((dispatch, costCentres) => {
  dispatch({ type: ACTION_UPDATE_COSTCENTRES, costCentres });
}, 300);

const mapDispatchToProps = {
  loadSettings: () => {
    const thunk = async (dispatch, getState, baseApiUrl) => {
      const { config } = getState();
      const { endpoints } = config;

      dispatch({ type: ACTION_LOAD_SETTINGS });

      const response = await getAccountSettings(baseApiUrl + endpoints.settings);

      if (response.ok) {
        const settings = await response.json();

        dispatch({ type: ACTION_LOAD_SETTINGS_SUCCESS, settings });
      } else {
        dispatch({ type: ACTION_LOAD_SETTINGS_FAILURE });
        dispatch(buildAndShowErrorToast('Something went wrong, your account settings cannot be shown.'));
      }
    };
    return thunk;
  },

  updateCostCentres: (costCentres: string) => {
    const thunk = (dispatch) => {
      dispatch({ type: ACTION_UPDATE_COSTCENTRES, costCentres });
      debouncedUpdateCostCentres(dispatch, costCentres);
    };
    return thunk;
  },

  updateSettings: () => {
    const thunk = async (dispatch, getState, baseApiUrl) => {
      const { settings, config } = getState();
      const { endpoints } = config;

      if (settings.isUpdating) {
        return;
      }

      dispatch({ type: ACTION_UPDATE_SETTINGS });

      const body = {
        isInvoicingEnabled: settings.isInvoicingEnabled,
        isPaymentRestrictedToInvoicing: settings.isPaymentRestrictedToInvoicing,
        isPONumberEnabled: settings.isPONumberEnabled,
        isPONumberRequired: settings.isPONumberRequired,
        costCentres: settings.costCentres,
        isCostCentreRequired: settings.isCostCentreRequired,
        currencies: settings.currencies,
      };

      let response = {};

      const NOINTERNET = 0;

      try {
        response = await updateAccountSettings(baseApiUrl + endpoints.settings, body);
      } catch (e) {
        // Network failure or Service unavailable
        response = { status: NOINTERNET, ok: false };
      }

      if (response.ok) {
        dispatch({ type: ACTION_UPDATE_SETTINGS_SUCCESS });
        dispatch(buildAndShowSuccessToast('Your settings have been saved successfully.'));
      } else {
        const genericErrorMessage = 'Something went wrong, your settings were not saved.';

        const errorMessage = {
          '403': 'You do not have permission to save these settings.',
          '409': 'You cannot save these settings because you are impersonating a user of a different account.',
          [NOINTERNET]: 'You cannot save these settings right now, please try again later.',
        };

        dispatch({ type: ACTION_UPDATE_SETTINGS_FAILURE });
        dispatch(buildAndShowErrorToast(errorMessage[response.status] || genericErrorMessage));
      }
    };
    return thunk;
  },

  toggleValue: (key) => {
    return { type: ACTION_TOGGLE, key };
  },

  toggleCurrency: (code) => {
    return { type: ACTION_TOGGLE_CURRENCY, code };
  },
};

const mapStateToProps = ({ settings }) => ({ ...settings });

const SettingsPage = connect(mapStateToProps, mapDispatchToProps)(Component);

export const settingsPageSliceReducer = createReducer(initialSettingPageState, {
  [ACTION_TOGGLE]: (state, { key }) => {
    return { ...state, [key]: !state[key] };
  },

  [ACTION_TOGGLE_CURRENCY]: (state, { code }) => {
    const revisedCurrencyList = state.currencies.map((currency) => {
      let { enabled } = currency;
      if (currency.code === code) {
        enabled = !currency.enabled;
      }
      return {
        ...currency,
        enabled,
      };
    });

    return {
      ...state,
      currencies: revisedCurrencyList,
    };
  },

  [ACTION_LOAD_SETTINGS]: (state) => ({ ...state, isLoading: true }),

  [ACTION_LOAD_SETTINGS_SUCCESS]: (state, { settings }) => {
    return {
      ...state,
      isLoading: false,
      ...settings,
    };
  },

  [ACTION_LOAD_SETTINGS_FAILURE]: (state) => {
    return { ...state };
  },

  [ACTION_UPDATE_SETTINGS]: (state) => ({ ...state, isUpdating: true }),

  [ACTION_UPDATE_SETTINGS_SUCCESS]: (state, { settings }) => {
    return { ...state, isUpdating: false, ...settings };
  },

  [ACTION_UPDATE_SETTINGS_FAILURE]: (state) => {
    return { ...state, isUpdating: false };
  },

  [ACTION_UPDATE_COSTCENTRES]: (state, { costCentres }) => {
    const mappedCostCentresArray = costCentres.map((label) => ({ label }));
    return { ...state, costCentres: mappedCostCentresArray };
  },
});

export default SettingsPage;

const ToggleInput = ({ onToggle, checked, label }) => {
  return (
    <div className="toggle-component">
      <input
        className="toggle-component__input"
        data-form="invoice"
        type="checkbox"
        id="invoice-checkbox"
        checked={checked}
        onChange={onToggle}
      />
      <label htmlFor="invoice-checkbox" className="toggle-component__btn" aria-label={label} />
    </div>
  );
};
